import React, { Suspense, useEffect, useState } from 'react';
import { ErrorBoundary, useErrorBoundary } from 'react-error-boundary';

type LazyResolverProps<Props = {}, T extends string = string> = {
  variant: T;
  components: { [K in T]: React.LazyExoticComponent<React.ComponentType<Props>> };
  fallback?: React.ReactNode;
  componentProps?: Props;
};

const NetworkCheck = () => {
  const { resetBoundary } = useErrorBoundary();
  useEffect(() => {
    const handleOnline = () => {
      resetBoundary();
    };

    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [ resetBoundary ]);

  return null;
};

export function LazyResolver<Props = {}, T extends string = string>({
  variant,
  components,
  fallback = null,
  componentProps,
}: LazyResolverProps<Props, T>) {
  const [ retryKey, setRetryKey ] = useState(0);

  const Component = components[variant];

  return (
    <ErrorBoundary
      FallbackComponent={NetworkCheck}
      onReset={() => {
        setRetryKey((prevKey) => prevKey + 1);
      }}
    >
      <Suspense fallback={fallback} key={retryKey}>
        <Component key={retryKey} {...(componentProps as any)} />
      </Suspense>
    </ErrorBoundary>
  );
}
